import { useState, useCallback, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  cpGetLineOAAudienceList,
  type LineFollowerProfile,
} from '@/lib/api/controlPlaneClient'
import { useAuthStore } from '@/lib/store/authStore'

export const LINE_OA_AUDIENCE_KEY = ['line-oa-audience'] as const

export function useLineOAAudience(options?: { enabled?: boolean }) {
  const activeStorefrontKey = useAuthStore((s) => s.activeStorefrontKey)
  const [profiles, setProfiles] = useState<LineFollowerProfile[]>([])
  const [nextToken, setNextToken] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)

  // Initial page fetch
  const query = useQuery({
    queryKey: [...LINE_OA_AUDIENCE_KEY, activeStorefrontKey],
    queryFn: () => cpGetLineOAAudienceList(),
    staleTime: 3 * 60 * 1000,
    enabled: options?.enabled !== false,
  })

  // Sync initial data into state
  useEffect(() => {
    if (query.data) {
      setProfiles(query.data.profiles)
      setNextToken(query.data.next_token)
      setHasMore(query.data.has_more)
    }
  }, [query.data])

  // Load more — append next page
  const loadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore || !nextToken) return

    setIsLoadingMore(true)
    try {
      const data = await cpGetLineOAAudienceList(nextToken)
      setProfiles((prev) => [...prev, ...data.profiles])
      setNextToken(data.next_token)
      setHasMore(data.has_more)
    } catch {
      // Swallow — user can retry via button
    } finally {
      setIsLoadingMore(false)
    }
  }, [isLoadingMore, hasMore, nextToken])

  // Refetch resets accumulated state
  const refetch = useCallback(async () => {
    setProfiles([])
    setNextToken(null)
    setHasMore(false)
    return query.refetch()
  }, [query])

  return {
    profiles,
    isLoading: query.isLoading,
    isLoadingMore,
    isError: query.isError,
    hasMore,
    loadMore,
    refetch,
  }
}
